import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { observabilityApi } from '../../../services/api';
import { ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Activity } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceDot,
  ReferenceLine, CartesianGrid, Legend, Area, AreaChart,
} from 'recharts';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#ec4899'];

const TableDrilldown: React.FC<{ tableId: number; onBack: () => void }> = ({ tableId, onBack }) => {
  const q = useQuery({
    queryKey: ['obs-table-bundle', tableId],
    queryFn: async () => (await observabilityApi.getTableBundle(tableId)).data.data,
    refetchInterval: 60000,
  });

  const d = q.data;

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-sm text-indigo-600 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to tables
      </button>

      {q.isLoading || !d ? (
        <div className="p-8 text-center text-sm text-cargo-muted">Loading...</div>
      ) : (
        <div className="space-y-5">
          {/* ── 1. Header with health + importance ── */}
          <Header d={d} />

          {/* ── 2,3. Volume + Freshness ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <VolumeChart d={d} />
            <FreshnessChart d={d} />
          </div>

          {/* ── 4. Null rates (multi-line) ── */}
          <NullRatesChart d={d} />

          {/* ── 5. Schema column count + drift ── */}
          <SchemaPanel d={d} />

          {/* ── 6. Incidents ── */}
          <IncidentsPanel d={d} />

          {/* ── 8. Recent anomalies ── */}
          <RecentAnomaliesPanel d={d} />
        </div>
      )}
    </div>
  );
};

/* ─────────────────── 1. Header ─────────────────── */
const Header: React.FC<{ d: any }> = ({ d }) => {
  const t = d.table || {};
  const status = d.health_status || 'healthy';
  const colors: Record<string, string> = {
    healthy: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-700',
    critical: 'bg-red-100 text-red-700',
  };
  const Icon = status === 'critical' ? AlertTriangle : status === 'warning' ? Activity : CheckCircle2;
  return (
    <div className="bg-white rounded-xl border border-cargo-border p-5 flex items-center gap-4">
      <div className="flex-1">
        <h2 className="text-xl font-semibold">
          {t.schema_name ? `${t.schema_name}.` : ''}{t.table_name}
        </h2>
        <div className="text-xs text-cargo-muted mt-1">
          Source: {t.source_id}
          {t.freshness_column && ` • Freshness col: ${t.freshness_column}`}
        </div>
      </div>
      <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold ${colors[status]}`}>
        <Icon className="w-4 h-4" /> {status.toUpperCase()}
      </span>
    </div>
  );
};

/* ─────────────────── 2. Volume ─────────────────── */
const VolumeChart: React.FC<{ d: any }> = ({ d }) => {
  const data = (d.row_count || []).map((p: any) => ({
    t: p.ts ? new Date(p.ts).toLocaleTimeString() : '', value: p.value,
  }));
  const growth = d.growth_rate_pct || 0;
  const volumeAnoms = (d.recent_anomalies || []).filter((a: any) => a.metric === 'row_count');
  return (
    <div className="bg-white rounded-xl border border-cargo-border p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">📦 Volume (Row Count)</h3>
        <div className={`text-xs font-semibold flex items-center gap-1 ${growth >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {growth >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {growth > 0 ? '+' : ''}{growth.toFixed(2)}%
        </div>
      </div>
      {data.length === 0 ? <EmptyChart /> : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="t" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip />
              <Area type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} fill="#6366f140" />
              {volumeAnoms.map((a: any, i: number) => (
                <ReferenceDot key={i}
                  x={new Date(a.detected_at).toLocaleTimeString()}
                  y={a.observed_value} r={6} fill="#ef4444" stroke="white" />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

/* ─────────────────── 3. Freshness ─────────────────── */
const FreshnessChart: React.FC<{ d: any }> = ({ d }) => {
  const data = (d.staleness_seconds || []).map((p: any) => ({
    t: p.ts ? new Date(p.ts).toLocaleTimeString() : '', value: p.value,
  }));
  const sla = d.sla_threshold_seconds;
  const freshAnoms = (d.recent_anomalies || []).filter((a: any) => a.metric === 'staleness_seconds');
  return (
    <div className="bg-white rounded-xl border border-cargo-border p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">🕐 Freshness (Staleness seconds)</h3>
        {sla && <div className="text-xs text-cargo-muted">SLA: {sla.toFixed(0)}s</div>}
      </div>
      {data.length === 0 ? <EmptyChart /> : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="t" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} dot={false} />
              {sla && <ReferenceLine y={sla} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'SLA', fill: '#f59e0b', fontSize: 10 }} />}
              {freshAnoms.map((a: any, i: number) => (
                <ReferenceDot key={i}
                  x={new Date(a.detected_at).toLocaleTimeString()}
                  y={a.observed_value} r={6} fill="#ef4444" stroke="white" />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

/* ─────────────────── 4a. Null Rates ─────────────────── */
const NullRatesChart: React.FC<{ d: any }> = ({ d }) => {
  const cols = (d.null_rates || []).slice(0, 8);
  const chartData = useMemo(() => {
    const tsMap: Record<string, any> = {};
    cols.forEach((c: any) => {
      (c.history || []).forEach((p: any) => {
        const key = p.ts ? new Date(p.ts).toLocaleTimeString() : '';
        if (!tsMap[key]) tsMap[key] = { t: key };
        tsMap[key][c.column] = p.value;
      });
    });
    return Object.values(tsMap);
  }, [cols]);
  return (
    <div className="bg-white rounded-xl border border-cargo-border p-5">
      <h3 className="font-semibold mb-3">📊 Null Rates per Column</h3>
      {chartData.length === 0 ? <EmptyChart /> : (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="t" fontSize={10} />
              <YAxis fontSize={10} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
              <Tooltip formatter={(v: any) => `${(v * 100).toFixed(2)}%`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {cols.map((c: any, i: number) => (
                <Line key={c.column} type="monotone" dataKey={c.column}
                      stroke={COLORS[i % COLORS.length]} strokeWidth={1.5} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
        {cols.map((c: any) => (
          <div key={c.column} className="p-2 border border-cargo-border rounded-lg">
            <div className="text-[11px] text-cargo-muted truncate">{c.column}</div>
            <div className={`text-sm font-semibold ${c.latest > 0.4 ? 'text-red-600' : c.latest > 0.1 ? 'text-amber-500' : 'text-emerald-600'}`}>
              {c.latest != null ? `${(c.latest * 100).toFixed(1)}%` : '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


/* ─────────────────── Schema ─────────────────── */
const SchemaPanel: React.FC<{ d: any }> = ({ d }) => {
  const data = (d.column_count || []).map((p: any) => ({
    t: p.ts ? new Date(p.ts).toLocaleTimeString() : '', value: p.value,
  }));
  const drifts = d.drift_events || [];
  return (
    <div className="bg-white rounded-xl border border-cargo-border p-5">
      <h3 className="font-semibold mb-3">🏗️ Schema — Column Count + Drift</h3>
      {data.length === 0 ? <EmptyChart /> : (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="t" fontSize={10} />
              <YAxis fontSize={10} />
              <Tooltip />
              <Line type="stepAfter" dataKey="value" stroke="#8b5cf6" strokeWidth={2} dot />
              {drifts.map((a: any, i: number) => (
                <ReferenceDot key={i}
                  x={new Date(a.detected_at).toLocaleTimeString()}
                  y={data[data.length - 1]?.value || 0} r={8} fill="#ef4444" stroke="white" />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      {drifts.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-semibold text-cargo-muted mb-1">Drift Events</div>
          <ul className="text-xs space-y-1">
            {drifts.slice(0, 5).map((a: any) => (
              <li key={a.id} className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                <span>{a.message}</span>
                <span className="ml-auto text-cargo-muted">{a.detected_at}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/* ─────────────────── Incidents ─────────────────── */
const IncidentsPanel: React.FC<{ d: any }> = ({ d }) => {
  const incidents = d.incidents || [];
  return (
    <div className="bg-white rounded-xl border border-cargo-border p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">📋 Incidents</h3>
        <span className="text-xs text-cargo-muted">{d.open_incidents_count || 0} open</span>
      </div>
      {incidents.length === 0 ? (
        <div className="text-xs text-cargo-muted">No incidents for this table.</div>
      ) : (
        <ul className="space-y-1.5">
          {incidents.slice(0, 10).map((i: any) => (
            <li key={i.id} className="flex items-center gap-2 text-sm py-1.5 border-b border-cargo-border last:border-b-0">
              <Sev sev={i.severity} />
              <span className="flex-1 truncate">{i.title}</span>
              <StatusChip status={i.status} />
              <span className="text-xs text-cargo-muted">{i.opened_at}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ─────────────────── 8. Recent Anomalies ─────────────────── */
const RecentAnomaliesPanel: React.FC<{ d: any }> = ({ d }) => {
  const anoms = d.recent_anomalies || [];
  return (
    <div className="bg-white rounded-xl border border-cargo-border p-5">
      <h3 className="font-semibold mb-3">🔔 Recent Anomalies</h3>
      {anoms.length === 0 ? (
        <div className="text-xs text-cargo-muted">No anomalies.</div>
      ) : (
        <ul className="space-y-1.5">
          {anoms.slice(0, 10).map((a: any) => (
            <li key={a.id} className="flex items-center gap-2 text-sm py-1.5 border-b border-cargo-border last:border-b-0">
              <Sev sev={a.severity} />
              <span className="flex-1">{a.message}</span>
              <span className="text-xs text-cargo-muted">{a.detected_at}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ─────────────────── Helpers ─────────────────── */
const EmptyChart = () => (
  <div className="h-48 flex items-center justify-center text-xs text-cargo-muted">
    Collecting data — check back in a minute
  </div>
);

const Sev: React.FC<{ sev: string }> = ({ sev }) => {
  const c = sev === 'high' ? 'bg-red-100 text-red-700' : sev === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700';
  return <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${c}`}>{sev}</span>;
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const m: Record<string, string> = {
    open: 'bg-red-100 text-red-700',
    triaged: 'bg-amber-100 text-amber-700',
    resolved: 'bg-emerald-100 text-emerald-700',
  };
  return <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${m[status] || 'bg-slate-100 text-slate-700'}`}>{status}</span>;
};

export default TableDrilldown;
